import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

// Styled components
const MacroItemContainer = styled.div`
  /* Glassmorphism base styles */
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  
  /* FIXED DIMENSIONS FOR CONSISTENCY */
  width: 100%;
  height: 140px;
  min-height: 140px;
  max-height: 140px;
  
  /* Layout and spacing */
  padding: 20px 16px;
  cursor: pointer;
  text-align: center;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  
  /* Ensure dropdowns stay within bounds */
  overflow: visible;
  box-sizing: border-box;
  
  /* Glass shadow effect */
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  
  /* Smooth transitions */
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;

  &:hover {
    /* Enhanced glass effect on hover */
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-4px) scale(1.02);
    box-shadow: 
      0 16px 40px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
    z-index: 2;
  }
  
  &:active {
    transform: translateY(-2px) scale(1.01);
    box-shadow: 
      0 12px 32px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }

  /* Add subtle glow effect */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 1px;
    background: linear-gradient(135deg, 
      rgba(255, 255, 255, 0.1) 0%, 
      rgba(255, 255, 255, 0.05) 50%, 
      rgba(255, 255, 255, 0.1) 100%);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }

  &:hover::before {
    opacity: 1;
  }
`;

const MacroIconContainer = styled.div`
  /* FIXED SIZE ICON CONTAINER FOR CONSISTENCY */
  width: 56px;
  height: 56px;
  min-width: 56px;
  min-height: 56px;
  flex-shrink: 0;
  
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  
  /* Glass background for icon */
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  
  /* Subtle inner glow */
  box-shadow: 
    inset 0 1px 0 rgba(255, 255, 255, 0.15),
    0 4px 16px rgba(0, 0, 0, 0.1);
  
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  /* Ensure emoji and icons are properly centered */
  & > img,
  & > span,
  & > div {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }

  /* Enhanced glow on parent hover */
  ${MacroItemContainer}:hover & {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 
      inset 0 1px 0 rgba(255, 255, 255, 0.2),
      0 6px 20px rgba(0, 0, 0, 0.15);
    transform: scale(1.05);
  }
`;

const MacroNameContainer = styled.div`
  /* FIXED TEXT STYLING FOR CONSISTENCY */
  font-size: 14px;
  line-height: 1.3;
  font-weight: 600;
  color: #fff;
  
  /* Fixed dimensions */
  width: 100%;
  height: 36px;
  min-height: 36px;
  max-height: 36px;
  
  padding: 0 8px;
  box-sizing: border-box;
  
  /* Center text vertically */
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* Text overflow handling */
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
  text-align: center;
  
  /* Limit to 2 lines */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  letter-spacing: 0.3px;
`;

const ControlButton = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  
  /* Glass effect for control buttons */
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  
  /* Subtle shadow */
  box-shadow: 
    0 2px 8px rgba(0, 0, 0, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0;
  transform: scale(0.8);
  
  svg {
    width: 14px;
    height: 14px;
  }
  
  /* Show on parent hover */
  ${MacroItemContainer}:hover & {
    opacity: 1;
    transform: scale(1);
  }
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 
      0 4px 12px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const DeleteButton = styled(ControlButton)`
  position: absolute;
  top: 10px;
  right: 10px;
  color: #ef4444;
  
  &:hover {
    background: rgba(239, 68, 68, 0.15);
    border-color: rgba(239, 68, 68, 0.3);
    color: #dc2626;
  }
`;

const EditButton = styled(ControlButton)`
  position: absolute;
  top: 10px;
  left: 10px;
  color: #6366f1;
  
  &:hover {
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.3);
    color: #4f46e5;
  }
`;


/**
 * A reusable component for rendering individual macro items
 */
const MacroItem = ({ 
  icon, 
  name, 
  onClick, 
  onContextMenu, 
  children, 
  showDelete, 
  onDelete,
  showEdit,
  onEdit,
  isPredefined,
  customIcon,
  ...props 
}) => {
  const handleDelete = (e) => {
    if (onDelete) {
      e.stopPropagation(); // Prevent triggering the macro click
      onDelete(e);
    }
  };

  const handleEdit = (e) => {
    if (onEdit) {
      e.stopPropagation(); // Prevent triggering the macro click
      onEdit(e);
    }
  };

  // Determine what to render for the icon
  let iconElement;
  if (customIcon !== undefined && customIcon !== null) {
    iconElement = customIcon;
  } else if (typeof icon === 'string') {
    iconElement = <span>{icon}</span>;
  } else {
    iconElement = icon;
  }

  return (
    <MacroItemContainer 
      onClick={onClick} 
      onContextMenu={onContextMenu} 
      title={name}
      {...props}
    >
      <>
        <MacroIconContainer>{iconElement}</MacroIconContainer>
        <MacroNameContainer>{name}</MacroNameContainer>
      </>
      {/* Render dropdown or other children with proper z-index */}
      {children}
      {showEdit && (
        <EditButton 
          onClick={handleEdit}
          title={isPredefined ? "Customize macro" : "Edit macro"}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </EditButton>
      )}
      {showDelete && (
        <DeleteButton 
          onClick={handleDelete}
          title="Delete macro"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </DeleteButton>
      )}
    </MacroItemContainer>
  );
};

MacroItem.propTypes = {
  icon: PropTypes.node,
  name: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onContextMenu: PropTypes.func,
  children: PropTypes.node,
  showDelete: PropTypes.bool,
  onDelete: PropTypes.func,
  showEdit: PropTypes.bool,
  onEdit: PropTypes.func,
  isPredefined: PropTypes.bool,
  customIcon: PropTypes.node
};

MacroItem.defaultProps = {
  onContextMenu: null,
  children: null,
  showDelete: false,
  onDelete: null,
  showEdit: false,
  onEdit: null,
  isPredefined: false,
  icon: null,
  customIcon: null
};

export default MacroItem; 